import { DataTag } from '@/components/DataTag/DataTag'
import {
  type FieldApi,
  FieldRow,
  handleNumberChange,
  handleStringChange,
  numberInputValue,
} from '@/components/form/formHelpers'
import {
  type CreateLicenseRequestBody,
  type LicenseResponseBody,
  invalidateOrgLicenses,
  useCreateOrgLicense,
} from '@/features/Licenses/api/licenseHooks'
import type { LicenseOrigin as ContractLicenseOrigin } from '@/generated/http-clients/v3ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  showOrUpdateToast,
} from '@/hooks/useToast/toastUtils'
import {
  Alert,
  Button,
  Dialog,
  Fieldset,
  HStack,
  Input,
  NativeSelect,
  Portal,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

/**
 * Create-license dialog hosted by `LicensesCard`.
 *
 * Two states inside one Chakra `Dialog.Root`: a TanStack-form-driven
 * input form, and a success view that shows the generated license key
 * + entitlements echo + validity window. The dialog stays open across
 * the swap so the operator can copy the key without losing context.
 *
 * Conventions enforced (see openspec/changes/add-slg-m1-backoffice-ui/
 * design.md and specs/license-creation/spec.md):
 *   - Decision 12: form is `@tanstack/react-form` + zod (the codebase
 *     convention). Submit is gated on `form.state.canSubmit`; ad-hoc
 *     `useState` + hand-written `validate()` is forbidden.
 *   - Decision 4 + license-creation/spec.md (both amended for #7086):
 *     `origin` is a three-option
 *     dropdown (`sales-led` default, `partner-led`, `internal`).
 *     `staff-led` was removed from the contract by hivemq-cloud PR #7086,
 *     so offering it produced a request the API rejects.
 *     `self-service` and `marketplace` are deliberately
 *     excluded — see Decision 4 for the rationale. The `reference`
 *     field's placeholder follows the chosen origin so the operator
 *     gets a hint about what value the auditor expects.
 *     `LicenseSource` was dropped from the contract by license-service
 *     PR #141.
 *   - Decision 5: validity dates submit as `int64` epoch ms via
 *     `Date.UTC(year, month, day)` — no intermediate `Date` object,
 *     no `.getTime()`.
 *   - Decision 14: pickers default to today (UTC) → today + 365d (UTC)
 *     on mount and on "Create another" reset.
 *   - Decision 16: human labels are "Valid from" / "Valid until".
 *   - Decision 9: a successful create invalidates queries with prefix
 *     `['api', 'v3', 'orgs', orgId, 'licenses']` so both the Org Details
 *     list and the disambiguation-card licence-count badge refresh.
 */

// The three `LicenseOrigin` values selectable in the backoffice.
// `self-service` is excluded (customer-driven; would mis-tag a
// backoffice creation); `marketplace` is excluded (auto-provisioned by
// AWS / GCP, not manual entry). See `design.md` Decision 4.
// `staff-led` was a fourth until hivemq-cloud PR #7086 removed it from the
// contract; it stayed in this list while the generated client was stale, so
// the dropdown offered a value the API rejects. The `satisfies` clause is what
// stops that recurring: the list is checked against the GENERATED contract
// type, so a value the contract drops fails the build here instead of shipping
// as a dropdown option the API refuses.
const BACKOFFICE_ORIGINS = [
  'sales-led',
  'partner-led',
  'internal',
] as const satisfies readonly ContractLicenseOrigin[]
type BackofficeOrigin = (typeof BACKOFFICE_ORIGINS)[number]

type Props = {
  orgId: string
  isOpen: boolean
  onClose: () => void
}

function todayUtcMs(): number {
  const now = new Date()
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
}

function oneYearOutUtcMs(): number {
  const now = new Date()
  return Date.UTC(now.getUTCFullYear() + 1, now.getUTCMonth(), now.getUTCDate())
}

function epochToInputDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

function inputDateToEpoch(value: string): number {
  const [y, m, d] = value.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

function makeFormSchema(t: (key: string) => string) {
  return z
    .object({
      name: z
        .string()
        .trim()
        .max(
          255,
          String(
            t('licenses.createLicenseDialog.validation.maxLength'),
          ).replace('{{max}}', '255'),
        ),
      description: z
        .string()
        .trim()
        .max(
          1000,
          String(
            t('licenses.createLicenseDialog.validation.maxLength'),
          ).replace('{{max}}', '1,000'),
        ),
      maxConnections: z.coerce
        .number()
        .int()
        .min(
          1,
          String(t('licenses.createLicenseDialog.validation.positiveNumber')),
        ),
      maxMessagesPerSecond: z.coerce
        .number()
        .int()
        .min(
          1,
          String(t('licenses.createLicenseDialog.validation.positiveNumber')),
        ),
      validityStart: z
        .string()
        .regex(
          ISO_DATE_REGEX,
          String(t('licenses.createLicenseDialog.validation.required')),
        ),
      validityEnd: z
        .string()
        .regex(
          ISO_DATE_REGEX,
          String(t('licenses.createLicenseDialog.validation.required')),
        ),
      origin: z.enum(BACKOFFICE_ORIGINS),
      reference: z
        .string()
        .min(1, String(t('licenses.createLicenseDialog.validation.required')))
        .max(
          255,
          String(
            t('licenses.createLicenseDialog.validation.maxLength'),
          ).replace('{{max}}', '255'),
        ),
    })
    .refine(
      (data) =>
        inputDateToEpoch(data.validityEnd) >
        inputDateToEpoch(data.validityStart),
      {
        message: String(t('licenses.createLicenseDialog.validation.dateRange')),
        path: ['validityEnd'],
      },
    )
}

function makeDefaultValues() {
  return {
    name: '',
    description: '',
    maxConnections: 1000,
    maxMessagesPerSecond: 10000,
    validityStart: epochToInputDate(todayUtcMs()),
    validityEnd: epochToInputDate(oneYearOutUtcMs()),
    origin: 'sales-led' as BackofficeOrigin,
    reference: '',
  }
}

const NUMBER_FORMAT = new Intl.NumberFormat('en-US')
const DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

export function CreateLicenseDialog({
  orgId,
  isOpen,
  onClose,
}: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()
  const [success, setSuccess] = useState<LicenseResponseBody | null>(null)

  // Ref used to break the circular dependency between createMutation (which
  // needs to fire a retry action) and form (which depends on createMutation).
  const retryRef = useRef<() => void>(() => {
    // replaced after form initialization, before any interaction
  })

  const createMutation = useCreateOrgLicense({
    onSuccess: (data) => {
      setSuccess(data)
      invalidateOrgLicenses(queryClient, orgId)
    },
    onError: (error) => {
      if (error.status !== 400 && error.status !== 502) {
        showOrUpdateToast('create-license-error', {
          ...ERROR_TOAST_OPTION,
          title: String(t('licenses.createLicenseDialog.error.title')),
          description: String(t('licenses.createLicenseDialog.error.generic')),
          action: {
            label: String(t('licenses.createLicenseDialog.error.retry')),
            onClick: () => retryRef.current(),
          },
        })
      }
    },
  })

  const form = useForm({
    defaultValues: makeDefaultValues(),
    validators: {
      onChange: makeFormSchema(t),
    },
    onSubmit: async ({ value }) => {
      // Zod's `.trim()` on the schema runs during parse (used to determine
      // whether `.max(N)` is satisfied after trimming). It does not, however,
      // mutate `field.state.value` — TanStack Form keeps the raw input in
      // form state. So the explicit trim here is what actually shapes the
      // request body. Removing either side would change behaviour.
      const trimmedName = value.name.trim()
      const trimmedDescription = value.description.trim()
      const body: CreateLicenseRequestBody = {
        entitlements: {
          maxConnections: Number(value.maxConnections),
          maxMessagesPerSecond: Number(value.maxMessagesPerSecond),
          // `seats` is still required by the contract but is unused: it always
          // defaults to 1 and nothing enforces it, so we no longer surface it
          // in the UI and send the default. See APP-1627.
          seats: 1,
        },
        validityStart: inputDateToEpoch(value.validityStart),
        validityEnd: inputDateToEpoch(value.validityEnd),
        origin: value.origin,
        reference: value.reference,
      }
      if (trimmedName) {
        body.name = trimmedName
      }
      if (trimmedDescription) {
        body.description = trimmedDescription
      }
      // mutateAsync always rethrows on error; onError handles it, so we catch
      // here to avoid unhandled promise rejections in callers.
      await createMutation
        .mutateAsync({ pathParams: { orgId }, body })
        .catch(() => {
          // handled by onError callback
        })
    },
  })

  retryRef.current = () => void form.handleSubmit()

  const handleClose = () => {
    setSuccess(null)
    createMutation.reset()
    // Pass fresh defaults so reopening the dialog resets dates to today, not
    // to the dates computed at mount (Decision 14).
    form.reset(makeDefaultValues())
    onClose()
  }

  const handleCreateAnother = () => {
    setSuccess(null)
    createMutation.reset()
    // Pass fresh defaults so "Create another" resets dates to today, not to
    // the dates computed at mount (Decision 14).
    form.reset(makeDefaultValues())
  }

  const error = createMutation.isError ? createMutation.error : null
  const inDialogError =
    error && (error.status === 400 || error.status === 502)
      ? {
          detail:
            error.payload?.detail ??
            error.message ??
            String(t('licenses.createLicenseDialog.error.generic')),
          correlationId:
            error.status === 502 ? error.payload?.correlationId : undefined,
        }
      : null

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) {
          handleClose()
        }
      }}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header pb={3}>
              <Dialog.Title>
                {success
                  ? t('licenses.createLicenseDialog.successTitle')
                  : t('licenses.createLicenseDialog.title', { orgId })}
              </Dialog.Title>
            </Dialog.Header>

            {success ? (
              <>
                <Dialog.Body>
                  <VStack align="start" gap={3}>
                    <Text fontSize="sm" color="text.text-subtle">
                      {t('licenses.createLicenseDialog.licenseKeyLabel')}
                    </Text>
                    <DataTag value={success.licenseKey}>
                      {success.licenseKey}
                    </DataTag>
                    {success.name && (
                      <Text fontSize="sm" color="text.text-subtle">
                        {t('licenses.createLicenseDialog.fields.name.label')}
                        {`: ${success.name}`}
                      </Text>
                    )}
                    {success.description && (
                      <Text
                        fontSize="sm"
                        color="text.text-subtle"
                        wordBreak="break-word"
                      >
                        {t(
                          'licenses.createLicenseDialog.fields.description.label',
                        )}
                        {`: ${success.description}`}
                      </Text>
                    )}
                    <Text fontSize="sm" color="text.text-subtle">
                      {t('licenses.createLicenseDialog.entitlementsLabel')}:
                      {` ${NUMBER_FORMAT.format(success.entitlements.maxConnections)} conn · ${NUMBER_FORMAT.format(success.entitlements.maxMessagesPerSecond)} msg/s`}
                    </Text>
                    <Text fontSize="sm" color="text.text-subtle">
                      {t('licenses.createLicenseDialog.validityLabel')}:
                      {` ${DATE_FORMAT.format(new Date(success.validityStart))} → ${DATE_FORMAT.format(new Date(success.validityEnd))}`}
                    </Text>
                    {success.origin && (
                      <Text fontSize="sm" color="text.text-subtle">
                        {t('licenses.createLicenseDialog.fields.origin.label')}
                        {`: ${success.origin}`}
                      </Text>
                    )}
                    {success.reference && (
                      <Text
                        fontSize="sm"
                        color="text.text-subtle"
                        wordBreak="break-all"
                      >
                        {t(
                          'licenses.createLicenseDialog.fields.reference.label',
                        )}
                        {`: ${success.reference}`}
                      </Text>
                    )}
                  </VStack>
                </Dialog.Body>
                <Dialog.Footer>
                  <HStack gap={2}>
                    <Button variant="outline" onClick={handleCreateAnother}>
                      {t('licenses.createLicenseDialog.createAnother')}
                    </Button>
                    <Button colorPalette="brand" onClick={handleClose}>
                      {t('licenses.createLicenseDialog.close')}
                    </Button>
                  </HStack>
                </Dialog.Footer>
              </>
            ) : (
              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  void form.handleSubmit()
                }}
              >
                <Dialog.Body>
                  <VStack align="stretch" gap={6}>
                    {inDialogError && (
                      <Alert.Root status="error">
                        <Alert.Indicator />
                        <Alert.Content>
                          <Alert.Title>
                            {t('licenses.createLicenseDialog.error.title')}
                          </Alert.Title>
                          <Alert.Description>
                            {inDialogError.detail}
                          </Alert.Description>
                          {inDialogError.correlationId && (
                            <Alert.Description
                              fontSize="xs"
                              color="text.text-subtle"
                            >
                              {`${t('licenses.createLicenseDialog.error.correlationId')} ${inDialogError.correlationId}`}
                            </Alert.Description>
                          )}
                        </Alert.Content>
                      </Alert.Root>
                    )}

                    <Fieldset.Root>
                      <Fieldset.Legend fontSize="md" fontWeight="semibold">
                        {t('licenses.createLicenseDialog.identityLegend')}
                      </Fieldset.Legend>
                      <Fieldset.Content>
                        <form.Field name="name">
                          {(field) => (
                            <FieldRow
                              field={field as FieldApi<unknown>}
                              label={String(
                                t(
                                  'licenses.createLicenseDialog.fields.name.label',
                                ),
                              )}
                              helperText={String(
                                t(
                                  'licenses.createLicenseDialog.fields.identityHelper',
                                ),
                              )}
                            >
                              <Input
                                placeholder={String(
                                  t(
                                    'licenses.createLicenseDialog.fields.name.placeholder',
                                  ),
                                )}
                                value={field.state.value}
                                onChange={handleStringChange(field)}
                                onBlur={field.handleBlur}
                              />
                            </FieldRow>
                          )}
                        </form.Field>
                        <form.Field name="description">
                          {(field) => (
                            <FieldRow
                              field={field as FieldApi<unknown>}
                              label={String(
                                t(
                                  'licenses.createLicenseDialog.fields.description.label',
                                ),
                              )}
                              helperText={String(
                                t(
                                  'licenses.createLicenseDialog.fields.identityHelper',
                                ),
                              )}
                            >
                              <Textarea
                                rows={3}
                                placeholder={String(
                                  t(
                                    'licenses.createLicenseDialog.fields.description.placeholder',
                                  ),
                                )}
                                value={field.state.value}
                                onChange={handleStringChange(field)}
                                onBlur={field.handleBlur}
                              />
                            </FieldRow>
                          )}
                        </form.Field>
                      </Fieldset.Content>
                    </Fieldset.Root>

                    <Fieldset.Root>
                      <Fieldset.Legend fontSize="md" fontWeight="semibold">
                        {t('licenses.createLicenseDialog.entitlementsLabel')}
                      </Fieldset.Legend>
                      <Fieldset.Content>
                        <form.Field name="maxConnections">
                          {(field) => (
                            <FieldRow
                              field={field as FieldApi<unknown>}
                              label={String(
                                t(
                                  'licenses.createLicenseDialog.fields.maxConnections.label',
                                ),
                              )}
                            >
                              <Input
                                type="number"
                                min={1}
                                value={numberInputValue(field.state.value)}
                                onChange={handleNumberChange(field)}
                                onBlur={field.handleBlur}
                              />
                            </FieldRow>
                          )}
                        </form.Field>
                        <form.Field name="maxMessagesPerSecond">
                          {(field) => (
                            <FieldRow
                              field={field as FieldApi<unknown>}
                              label={String(
                                t(
                                  'licenses.createLicenseDialog.fields.maxMessagesPerSecond.label',
                                ),
                              )}
                            >
                              <Input
                                type="number"
                                min={1}
                                value={numberInputValue(field.state.value)}
                                onChange={handleNumberChange(field)}
                                onBlur={field.handleBlur}
                              />
                            </FieldRow>
                          )}
                        </form.Field>
                      </Fieldset.Content>
                    </Fieldset.Root>

                    <Fieldset.Root>
                      <Fieldset.Legend fontSize="md" fontWeight="semibold">
                        {t('licenses.createLicenseDialog.validityLabel')}
                      </Fieldset.Legend>
                      <Fieldset.Content>
                        <HStack gap={3} align="flex-start">
                          <form.Field name="validityStart">
                            {(field) => (
                              <FieldRow
                                field={field as FieldApi<unknown>}
                                label={String(
                                  t(
                                    'licenses.createLicenseDialog.fields.validFrom.label',
                                  ),
                                )}
                              >
                                <Input
                                  type="date"
                                  value={field.state.value}
                                  onChange={handleStringChange(field)}
                                  onBlur={field.handleBlur}
                                />
                              </FieldRow>
                            )}
                          </form.Field>
                          <form.Field name="validityEnd">
                            {(field) => (
                              <FieldRow
                                field={field as FieldApi<unknown>}
                                label={String(
                                  t(
                                    'licenses.createLicenseDialog.fields.validTo.label',
                                  ),
                                )}
                              >
                                <Input
                                  type="date"
                                  value={field.state.value}
                                  onChange={handleStringChange(field)}
                                  onBlur={field.handleBlur}
                                />
                              </FieldRow>
                            )}
                          </form.Field>
                        </HStack>
                      </Fieldset.Content>
                    </Fieldset.Root>

                    <Fieldset.Root>
                      <Fieldset.Legend fontSize="md" fontWeight="semibold">
                        {t('licenses.createLicenseDialog.originLegend')}
                      </Fieldset.Legend>
                      <Fieldset.Content>
                        <form.Field name="origin">
                          {(field) => (
                            <FieldRow
                              field={field as FieldApi<unknown>}
                              label={String(
                                t(
                                  'licenses.createLicenseDialog.fields.origin.label',
                                ),
                              )}
                            >
                              <NativeSelect.Root>
                                <NativeSelect.Field
                                  value={field.state.value}
                                  onChange={(event) =>
                                    field.handleChange(
                                      event.target.value as BackofficeOrigin,
                                    )
                                  }
                                  onBlur={field.handleBlur}
                                >
                                  {BACKOFFICE_ORIGINS.map((origin) => (
                                    <option key={origin} value={origin}>
                                      {String(
                                        t(
                                          `licenses.createLicenseDialog.fields.origin.options.${origin}`,
                                        ),
                                      )}
                                    </option>
                                  ))}
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                              </NativeSelect.Root>
                            </FieldRow>
                          )}
                        </form.Field>
                        <form.Subscribe
                          selector={(state) =>
                            state.values.origin as BackofficeOrigin
                          }
                        >
                          {(currentOrigin) => (
                            <form.Field name="reference">
                              {(field) => (
                                <FieldRow
                                  field={field as FieldApi<unknown>}
                                  label={String(
                                    t(
                                      'licenses.createLicenseDialog.fields.reference.label',
                                    ),
                                  )}
                                >
                                  <Input
                                    placeholder={String(
                                      t(
                                        `licenses.createLicenseDialog.fields.reference.placeholders.${currentOrigin}`,
                                      ),
                                    )}
                                    value={field.state.value}
                                    onChange={handleStringChange(field)}
                                    onBlur={field.handleBlur}
                                  />
                                </FieldRow>
                              )}
                            </form.Field>
                          )}
                        </form.Subscribe>
                      </Fieldset.Content>
                    </Fieldset.Root>
                  </VStack>
                </Dialog.Body>
                <Dialog.Footer>
                  <HStack gap={2}>
                    <Button variant="outline" onClick={handleClose}>
                      {t('licenses.createLicenseDialog.cancel')}
                    </Button>
                    <form.Subscribe
                      selector={(state) => [
                        state.canSubmit,
                        state.isSubmitting,
                      ]}
                    >
                      {([canSubmit, isSubmitting]) => (
                        <Button
                          type="submit"
                          colorPalette="brand"
                          loading={
                            Boolean(isSubmitting) || createMutation.isPending
                          }
                          disabled={!canSubmit}
                        >
                          {t('licenses.createLicenseDialog.submit')}
                        </Button>
                      )}
                    </form.Subscribe>
                  </HStack>
                </Dialog.Footer>
              </form>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
